/**
 * VerificationTester — the verification console inside the LexisNexis FraudPoint module
 * (BACKLOG-1743, Bill 2026-10-06; modernized + OFAC transparency, Bill 2026-10-07).
 *
 * Risk Management runs the SAME LexisNexis identity check (InstantID + FraudPoint + Emailage via
 * the DDP API) and the SAME OFAC check (Core Director OFACSrch + the bank's Treasury SDN copy)
 * the officer app runs — separately or together — and reads, in plain language, what was
 * checked, what came back, and why the app would call it clear, a historical match or a hit.
 * Every run re-reads the CURRENT saved settings on the server, so a change made on this screen
 * shows on the next run. Runs are reads and screens only: no cart, CIF, account or Core
 * Director write, and no e-mail.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  loadCifPrefill,
  loadExplain,
  runLexisNexisTest,
  runOfacTest,
  saveRiskSettingWithReason,
  searchCustomers,
  type CustomerHit,
  type ExplainResponse,
  type LnTestResponse,
  type OfacTestResponse,
  type TestSubject,
} from '@/services/verificationTestApi';

import { Banner, Disclosure, Field, Panel, Pill, SectionTitle, SettingsTable, fmtDate } from './consoleUi';
import { LnResultPanel, bandTone } from './LnResultPanel';
import { OfacResultPanel } from './OfacResultPanel';
import { RunCompare } from './RunCompare';

const STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME',
  'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI',
  'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'PR', 'GU', 'VI', 'AS', 'MP',
];

type Env = 'production' | 'sandbox';
type NameType = 'Indv' | 'Org';
type FieldKey = keyof TestSubject;

interface RunRecord {
  id: number;
  at: Date;
  screenedName: string;
  environment: Env;
  nameType: NameType;
  cifId: string | null;
  subject: TestSubject;
  purpose: string;
  checks: { ln: boolean; ofac: boolean };
  ln?: LnTestResponse;
  lnError?: string;
  ofac?: OfacTestResponse;
  ofacError?: string;
}

const EMPTY: TestSubject = {};
/** Keys the server's identity.verify.requiredInputs may name (LnVerifyInputRules). */
const FIELD_KEYS: FieldKey[] = ['firstName', 'middleInitial', 'lastName', 'ssn', 'dob', 'street', 'city', 'state', 'zip', 'phone', 'email'];
const digits = (v: string | undefined): string => (v ?? '').replace(/\D/g, '');

/** Mirrors the officer app's screening name (ofacAutoScan.buildOfacScreenName): first + middle + last. */
function screenName(s: TestSubject, nameType: NameType, businessName: string): string {
  if (nameType === 'Org') return businessName.trim();
  return [s.firstName, s.middleInitial, s.lastName].map((x) => (x ?? '').trim()).filter(Boolean).join(' ');
}

export function VerificationTester(): JSX.Element {
  const [explain, setExplain] = useState<ExplainResponse | null>(null);
  const [explainErr, setExplainErr] = useState<string | null>(null);
  const reloadExplain = useCallback((): void => {
    setExplainErr(null);
    loadExplain().then(setExplain).catch((e: unknown) => setExplainErr(e instanceof Error ? e.message : 'Could not load.'));
  }, []);
  useEffect(() => { reloadExplain(); }, [reloadExplain]);

  // While a refresh of the SDN copy runs, re-read every 10 s so it shows finishing.
  const refreshing = explain?.ofac.lastSyncRun?.status === 'in-progress';
  useEffect(() => {
    if (!refreshing) return undefined;
    const id = window.setInterval(reloadExplain, 10000);
    return () => window.clearInterval(id);
  }, [refreshing, reloadExplain]);

  // ── form ───────────────────────────────────────────────────────────────
  const [environment, setEnvironment] = useState<Env>('production');
  const [doLn, setDoLn] = useState(true);
  const [doOfac, setDoOfac] = useState(true);
  const [nameType, setNameType] = useState<NameType>('Indv');
  const [businessName, setBusinessName] = useState('');
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<CustomerHit[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [cifId, setCifId] = useState<string | null>(null);
  const [coreLast4, setCoreLast4] = useState<string | null>(null);
  const [subject, setSubject] = useState<TestSubject>(EMPTY);
  const [purpose, setPurpose] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // ── history ────────────────────────────────────────────────────────────
  const [runs, setRuns] = useState<RunRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [compareIds, setCompareIds] = useState<number[]>([]);

  const requiredLn = useMemo(() => {
    const csv = explain?.lexisNexis.requiredInputs ?? 'dob,phone';
    return new Set<string>(['firstName', 'lastName', ...csv.split(',').map((x) => x.trim()).filter(Boolean)]);
  }, [explain]);

  const set = (k: FieldKey, v: string): void => setSubject((s) => ({ ...s, [k]: v }));

  const errors = useMemo(() => {
    const e: Partial<Record<FieldKey | 'businessName' | 'purpose' | 'checks', string>> = {};
    const has = (k: FieldKey): boolean => (subject[k] ?? '').trim().length > 0;
    if (!doLn && !doOfac) e.checks = 'Pick at least one check.';
    if (doLn) {
      for (const k of requiredLn) {
        if (k === 'ssn' || !FIELD_KEYS.includes(k as FieldKey)) continue;
        if (!has(k as FieldKey)) e[k as FieldKey] = 'Required for the LexisNexis check.';
      }
      if (!cifId && digits(subject.ssn).length !== 9) e.ssn = 'Enter the 9-digit SSN (or look up a customer).';
    }
    if (doOfac && nameType === 'Indv' && (!has('firstName') || !has('lastName'))) {
      e.firstName = e.firstName ?? 'First and last name are needed for the OFAC check.';
      e.lastName = e.lastName ?? 'Required.';
    }
    if (doOfac && nameType === 'Org' && businessName.trim().length < 2) e.businessName = 'Enter the business name to screen.';
    if (has('dob') && !/^\d{4}-\d{2}-\d{2}$/.test(subject.dob ?? '')) e.dob = 'Use the date picker (YYYY-MM-DD).';
    if (cifId && (subject.ssn ?? '').length > 0 && digits(subject.ssn).length !== 9) e.ssn = 'Type all 9 digits to replace the SSN on file, or leave it blank.';
    if (has('zip') && !/^\d{5}(-?\d{4})?$/.test((subject.zip ?? '').trim())) e.zip = '5-digit ZIP (ZIP+4 allowed).';
    if (has('phone') && digits(subject.phone).length !== 10) e.phone = '10-digit phone number.';
    if (has('email') && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((subject.email ?? '').trim())) e.email = 'Not a valid e-mail address.';
    if (purpose.trim().length < 5) e.purpose = 'Say why you are running this (at least 5 characters) — it is recorded.';
    return e;
  }, [subject, doLn, doOfac, nameType, businessName, purpose, cifId, requiredLn]);
  const valid = Object.keys(errors).length === 0;
  const showErr = (k: keyof typeof errors): string | null => (touched ? errors[k] ?? null : null);

  const search = async (): Promise<void> => {
    if (query.trim().length < 2) return;
    setSearching(true); setErr(null); setHits(null);
    try {
      const found = await searchCustomers(query.trim());
      setHits(found);
      if (found.length === 1) await pick(found[0]);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Search failed.');
    } finally {
      setSearching(false);
    }
  };

  const pick = async (h: CustomerHit): Promise<void> => {
    setErr(null);
    try {
      const s = await loadCifPrefill(h.cifId);
      setCifId(h.cifId);
      setCoreLast4(s.taxIdLast4);
      setSubject({
        firstName: s.firstName ?? '', middleInitial: s.middleInitial ?? '', lastName: s.lastName ?? '',
        dob: s.dob ?? '', street: s.street ?? '', city: s.city ?? '', state: s.state ?? '', zip: s.zip ?? '',
        phone: s.phone ?? '', email: s.email ?? '', ssn: '',
      });
      setHits(null);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not load that customer.');
    }
  };

  const clearCustomer = (): void => {
    setCifId(null); setCoreLast4(null); setSubject(EMPTY); setHits(null);
  };

  const execute = async (rec: Omit<RunRecord, 'id' | 'at' | 'ln' | 'lnError' | 'ofac' | 'ofacError'>): Promise<void> => {
    setBusy(true); setErr(null);
    const id = (runs[0]?.id ?? 0) + 1;
    const lnP = rec.checks.ln
      ? runLexisNexisTest(rec.cifId
        ? { cifId: rec.cifId, subject: rec.subject, purpose: rec.purpose, environment: rec.environment }
        : { subject: rec.subject, purpose: rec.purpose, environment: rec.environment })
      : Promise.resolve(undefined);
    const ofP = rec.checks.ofac
      ? runOfacTest({ name: rec.screenedName, nameType: rec.nameType, purpose: rec.purpose })
      : Promise.resolve(undefined);
    const [lnR, ofR] = await Promise.allSettled([lnP, ofP]);
    const run: RunRecord = {
      ...rec, id, at: new Date(),
      ln: lnR.status === 'fulfilled' ? lnR.value : undefined,
      lnError: lnR.status === 'rejected' ? (lnR.reason instanceof Error ? lnR.reason.message : 'The check failed.') : undefined,
      ofac: ofR.status === 'fulfilled' ? ofR.value : undefined,
      ofacError: ofR.status === 'rejected' ? (ofR.reason instanceof Error ? ofR.reason.message : 'The search failed.') : undefined,
    };
    setRuns((prev) => [run, ...prev].slice(0, 12));
    setSelectedId(id);
    setBusy(false);
    reloadExplain();
  };

  const run = (): void => {
    setTouched(true);
    if (!valid) return;
    void execute({
      screenedName: screenName(subject, nameType, businessName),
      environment, nameType, cifId, subject: { ...subject }, purpose: purpose.trim(),
      checks: { ln: doLn, ofac: doOfac },
    });
  };

  const rerun = (r: RunRecord): void => {
    void execute({
      screenedName: r.screenedName, environment: r.environment, nameType: r.nameType, cifId: r.cifId,
      subject: r.subject, purpose: `${r.purpose} (re-run with current settings)`, checks: r.checks,
    });
  };

  const toggleCompare = (id: number): void =>
    setCompareIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id].slice(-2)));

  const selected = runs.find((r) => r.id === selectedId) ?? runs[0] ?? null;
  const compared = compareIds.map((id) => runs.find((r) => r.id === id)).filter((r): r is RunRecord => !!r);

  const text = (k: FieldKey, label: string, opts: { type?: string; placeholder?: string; hint?: string; inputMode?: 'numeric' | 'tel' | 'email' } = {}): JSX.Element => {
    const required = doLn && requiredLn.has(k);
    return (
      <Field label={label} htmlFor={`vc-${k}`} required={required} hint={opts.hint} error={showErr(k)}>
        <input
          id={`vc-${k}`}
          type={opts.type ?? 'text'}
          inputMode={opts.inputMode}
          value={subject[k] ?? ''}
          placeholder={opts.placeholder}
          autoComplete="off"
          onChange={(ev) => set(k, ev.target.value)}
        />
      </Field>
    );
  };

  return (
    <div className="vc" data-tour="ln-tester">
      <SourcesPanel explain={explain} error={explainErr} onReload={reloadExplain} />

      <Panel
        title="Run a check"
        actions={environment === 'production'
          ? <Pill tone="warn">Production — live, billed LexisNexis calls</Pill>
          : <Pill tone="info">LexisNexis test environment</Pill>}
      >
        <div className="vc-row" data-tour="ln-run-check">
          <Field label="Checks to run" htmlFor="vc-check-ln" error={showErr('checks')}>
            <div className="vc-row" style={{ gap: 'var(--sp-9)' }}>
              <label className="vc-toggle"><input id="vc-check-ln" type="checkbox" checked={doLn} onChange={(e) => setDoLn(e.target.checked)} /> LexisNexis identity check</label>
              <label className="vc-toggle"><input type="checkbox" checked={doOfac} onChange={(e) => setDoOfac(e.target.checked)} /> OFAC screening</label>
            </div>
          </Field>
          <Field label="LexisNexis environment" htmlFor="vc-env">
            <select id="vc-env" value={environment} disabled={!doLn} onChange={(e) => setEnvironment(e.target.value === 'sandbox' ? 'sandbox' : 'production')}>
              <option value="production">Production (live LexisNexis, billed)</option>
              <option value="sandbox">LexisNexis test environment (test identities)</option>
            </select>
          </Field>
          <Field label="OFAC name type" htmlFor="vc-nametype">
            <div className="vc-segment" id="vc-nametype" role="group" aria-label="OFAC name type">
              <button type="button" aria-pressed={nameType === 'Indv'} disabled={!doOfac} onClick={() => setNameType('Indv')}>Person</button>
              <button type="button" aria-pressed={nameType === 'Org'} disabled={!doOfac} onClick={() => setNameType('Org')}>Business</button>
            </div>
          </Field>
        </div>

        {doLn && environment === 'production' && (
          <Banner tone="warn">
            Every production LexisNexis run is a real, billed inquiry. It also adds to the person&rsquo;s &ldquo;search velocity&rdquo;,
            which lowers their FraudPoint score for a while — use the test environment for repeated what-if runs.
          </Banner>
        )}

        <div>
          <SectionTitle>Find a Core Director customer (optional)</SectionTitle>
          <div className="vc-row">
            <div style={{ flex: '1 1 300px' }}>
              <Field label="CIF number, or name (Last, First)" htmlFor="vc-query">
                <input id="vc-query" value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void search(); }} />
              </Field>
            </div>
            <button type="button" className="vc-btn vc-btn--ghost" disabled={searching || query.trim().length < 2} onClick={() => void search()}>
              {searching ? 'Searching…' : 'Search'}
            </button>
            {cifId && <button type="button" className="vc-btn vc-btn--ghost" onClick={clearCustomer}>Clear — enter details instead</button>}
          </div>
          {hits && (hits.length === 0 ? <p className="vc-hint" style={{ marginTop: 6 }}>No customers found.</p> : (
            <div className="vc-table-wrap" style={{ marginTop: 8 }}>
              <table className="vc-table">
                <thead><tr><th>Name</th><th>CIF</th><th>Type</th><th>Tax ID</th><th /></tr></thead>
                <tbody>
                  {hits.map((h) => (
                    <tr key={h.cifId}>
                      <td>{h.name}</td><td>{h.cifId}</td><td>{h.custType}</td><td>…{h.taxIdLast4 ?? '—'}</td>
                      <td><button type="button" className="vc-btn vc-btn--ghost vc-btn--sm" onClick={() => void pick(h)}>Use</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {cifId && (
            <p className="vc-hint" style={{ marginTop: 6 }}>
              Filled from Core Director CIF {cifId}. Change any field to see how the checks respond; unchanged fields are sent as Core Director holds them.
              Nothing is written back to Core Director.
            </p>
          )}
        </div>

        <div>
          <SectionTitle>Person to check</SectionTitle>
          <div className="vc-grid">
            {text('firstName', 'First name')}
            {text('middleInitial', 'Middle initial')}
            {text('lastName', 'Last name')}
            {text('dob', 'Date of birth', { type: 'date' })}
            <Field
              label="SSN"
              htmlFor="vc-ssn"
              required={doLn && !cifId}
              hint={cifId ? `On file: •••-••-${coreLast4 ?? '????'} — type 9 digits to change it` : 'Digits only; never shown again'}
              error={showErr('ssn')}
            >
              <input id="vc-ssn" type="password" inputMode="numeric" autoComplete="off" value={subject.ssn ?? ''} onChange={(e) => set('ssn', e.target.value)} />
            </Field>
            {text('phone', 'Phone', { inputMode: 'tel', placeholder: '573 555 0100' })}
            {text('email', 'E-mail', { inputMode: 'email' })}
          </div>
          <div className="vc-grid" style={{ marginTop: 'var(--sp-6)' }}>
            {text('street', 'Street')}
            {text('city', 'City')}
            <Field label="State" htmlFor="vc-state" required={doLn && requiredLn.has('state')} error={showErr('state')}>
              <select id="vc-state" value={subject.state ?? ''} onChange={(e) => set('state', e.target.value)}>
                <option value="">—</option>
                {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                {subject.state && !STATES.includes(subject.state) && <option value={subject.state}>{subject.state}</option>}
              </select>
            </Field>
            {text('zip', 'ZIP', { inputMode: 'numeric' })}
          </div>
          {doOfac && nameType === 'Org' && (
            <div className="vc-grid vc-grid--wide" style={{ marginTop: 'var(--sp-6)' }}>
              <Field label="Business name to screen" htmlFor="vc-biz" required error={showErr('businessName')}>
                <input id="vc-biz" value={businessName} onChange={(e) => setBusinessName(e.target.value)} />
              </Field>
            </div>
          )}
          {doOfac && (
            <p className="vc-hint" style={{ marginTop: 6 }}>
              OFAC screens the name only: <strong>{screenName(subject, nameType, businessName) || '—'}</strong>
            </p>
          )}
        </div>

        <Field label="Reason for this check" htmlFor="vc-purpose" required hint="Recorded with every run." error={showErr('purpose')}>
          <input id="vc-purpose" value={purpose} onChange={(e) => setPurpose(e.target.value)} placeholder="e.g. Checking how a changed address affects the result" />
        </Field>

        <div className="vc-footer">
          {touched && !valid && <span className="vc-footer__left vc-field__err">Fix the highlighted fields.</span>}
          <button type="button" className="vc-btn vc-btn--primary" disabled={busy} onClick={run}>
            {busy ? 'Running…' : doLn && doOfac ? 'Run both checks' : doLn ? 'Run LexisNexis check' : 'Run OFAC check'}
          </button>
        </div>
        {err && <Banner tone="err">{err}</Banner>}
      </Panel>

      {runs.length > 0 && (
        <Panel title={`Runs this session (${runs.length})`} tone="muted" actions={<span className="vc-muted">Kept until you leave this page</span>}>
          <ul className="vc-history">
            {runs.map((r) => {
              const band = r.ln?.fraudPoint?.band ?? null;
              return (
                <li key={r.id} className={selected?.id === r.id ? 'is-selected' : undefined}>
                  <button type="button" className="vc-history__main" onClick={() => setSelectedId(r.id)}>
                    <strong>#{r.id}</strong> {r.at.toLocaleTimeString()} · {r.screenedName || '—'}
                    <span className="vc-muted"> · {r.purpose}</span>
                  </button>
                  {r.checks.ln && (r.lnError ? <Pill tone="err">LexisNexis failed</Pill>
                    : r.ln?.success ? <Pill tone={bandTone(band)}>FraudPoint {r.ln.fraudPoint?.score ?? '—'} {band ?? ''}</Pill>
                    : <Pill tone="err">LexisNexis: no result</Pill>)}
                  {r.checks.ofac && (r.ofacError ? <Pill tone="err">OFAC failed</Pill>
                    : <Pill tone={r.ofac?.transparency?.verdict.tone ?? 'muted'}>{r.ofac?.transparency?.verdict.officerPill ?? r.ofac?.status}</Pill>)}
                  <label className="vc-toggle" style={{ fontSize: 12 }}>
                    <input type="checkbox" checked={compareIds.includes(r.id)} onChange={() => toggleCompare(r.id)} /> Compare
                  </label>
                  <button type="button" className="vc-btn vc-btn--ghost vc-btn--sm" disabled={busy} onClick={() => rerun(r)}
                    title={r.checks.ln && r.environment === 'production' ? 'Runs a new, billed LexisNexis inquiry' : undefined}>
                    Run again with current settings
                  </button>
                </li>
              );
            })}
          </ul>
          {compareIds.length === 1 && <p className="vc-hint">Tick one more run to compare.</p>}
        </Panel>
      )}

      {compared.length === 2 && (
        <Panel title={`Compare run #${Math.min(compared[0].id, compared[1].id)} with run #${Math.max(compared[0].id, compared[1].id)}`}
          actions={<button type="button" className="vc-btn vc-btn--link" onClick={() => setCompareIds([])}>Close</button>}>
          <RunCompare a={compared[0]} b={compared[1]} />
        </Panel>
      )}

      {selected && (
        <>
          {selected.checks.ln && (selected.lnError
            ? <Panel title="LexisNexis identity check" tone="err"><Banner tone="err">{selected.lnError}</Banner></Panel>
            : selected.ln && <LnResultPanel r={selected.ln} />)}
          {selected.checks.ofac && (selected.ofacError
            ? <Panel title="OFAC screening" tone="err"><Banner tone="err">{selected.ofacError}</Banner></Panel>
            : selected.ofac && <OfacResultPanel r={selected.ofac} />)}
        </>
      )}
    </div>
  );
}

// ── What is checked, against what (and the settings that change it) ─────────────────

function SourcesPanel({ explain, error, onReload }: { explain: ExplainResponse | null; error: string | null; onReload: () => void }): JSX.Element {
  return (
    <Panel
      title="What is checked, and against what"
      actions={<button type="button" className="vc-btn vc-btn--ghost vc-btn--sm" onClick={onReload}>Refresh</button>}
    >
      {error && <Banner tone="err">{error}</Banner>}
      {!explain && !error && <p className="vc-hint">Loading…</p>}
      {explain && <SourcesBody e={explain} onSaved={onReload} />}
    </Panel>
  );
}

function SourcesBody({ e, onSaved }: { e: ExplainResponse; onSaved: () => void }): JSX.Element {
  const ln = e.lexisNexis;
  const o = e.ofac;
  const svc = o.searchService;
  const cov = o.coverage;
  const running = o.lastSyncRun?.status === 'in-progress';
  return (
    <>
      <div className="vc-two">
        <div>
          <SectionTitle>LexisNexis identity check</SectionTitle>
          <dl className="vc-kv">
            <dt>Products</dt><dd>{ln.products.join(' · ')}</dd>
            <dt>Policy</dt><dd><span className="vc-mono">{ln.policyName}</span> in production · <span className="vc-mono">{ln.policyNameSandbox}</span> in the test environment</dd>
            <dt>Request type</dt><dd className="vc-mono">{ln.serviceType} / {ln.eventType}</dd>
            <dt>Officer use</dt>
            <dd>
              API {ln.enabled === 'true' ? 'on' : 'off'}
              {ln.enabledOfficers ? ` for officers ${ln.enabledOfficers}` : ' — no officers enabled; they use the LN-Bridge web report'}
            </dd>
            <dt>Must carry</dt><dd>{ln.requiredInputs || 'nothing extra'} (plus name and SSN)</dd>
            <dt>Watchlist</dt><dd>Not part of this check — OFAC screening is the check on the right.</dd>
            <dt>What decides</dt>
            <dd>
              The FraudPoint score only: {e.fraudPoint.lowFloor}+ proceeds; {e.fraudPoint.moderateFloor}–{e.fraudPoint.lowFloor - 1} needs follow-up
              documents; below {e.fraudPoint.moderateFloor} is declined.
              {e.fraudPoint.enforcement && <> When a requirement is not met: <span className="vc-mono">{e.fraudPoint.enforcement.strategy}</span>{e.fraudPoint.enforcement.isSubtypeOverride ? ' (set for Individual)' : ' (gate default)'}.</>}
              <div className="vc-muted">Market: {e.fraudPoint.marketBasis}.</div>
            </dd>
          </dl>
        </div>
        <div>
          <SectionTitle>OFAC screening</SectionTitle>
          <dl className="vc-kv">
            <dt>Service</dt><dd>{svc.name}</dd>
            <dt>List searched</dt><dd>{svc.listsSearched}</dd>
            <dt>Kind of matching</dt><dd>{svc.matching}</dd>
            <dt>Match score</dt><dd>{svc.score}</dd>
            <dt>Sent</dt><dd>{svc.input} Up to {svc.maxRecords} possible matches.</dd>
            <dt>Then</dt><dd>Each possible match&rsquo;s ID is looked up in the bank&rsquo;s copy of Treasury&rsquo;s SDN list: current listing = stop and review; no longer listed = proceed, logged.</dd>
          </dl>
          <Disclosure summary="Source: JHA's OFAC Search reference">
            <p className="vc-hint">{svc.listsSource}</p>
            <p className="vc-hint">{svc.matchingSource}</p>
            <p className="vc-hint">{svc.nameTypeNote}</p>
          </Disclosure>
        </div>
      </div>

      <div>
        <SectionTitle>Bank&rsquo;s SDN copy</SectionTitle>
        {cov.totalEntities === 0 && !running && (
          <Banner tone="err">The copy is empty, so possible matches cannot be sorted into current / no longer listed and are all treated as current.</Banner>
        )}
        {o.cacheSyncFailed === 'true' && <Banner tone="err">The last refresh from Treasury failed.</Banner>}
        <dl className="vc-kv">
          <dt>Entries</dt><dd>{cov.totalEntities.toLocaleString()} ({cov.currentListEntities.toLocaleString()} from Treasury&rsquo;s current list file) · {cov.aliasRows.toLocaleString()} aliases</dd>
          <dt>Lists</dt>
          <dd>{cov.lists.length === 0 ? 'None recorded' : <span className="vc-chips">{cov.lists.map((l) => <span key={l.name} className="vc-chip">{l.name} · {l.count.toLocaleString()}</span>)}</span>}</dd>
          <dt>Treasury data as of</dt><dd>{fmtDate(cov.treasuryDataAsOf, true)}</dd>
          <dt>Last refresh</dt>
          <dd>
            {o.lastSyncRun ? <>{fmtDate(o.lastSyncRun.startedAt)} — {o.lastSyncRun.status}{o.lastSyncRun.entitiesUpserted != null ? `, ${o.lastSyncRun.entitiesUpserted} updated` : ''}{o.lastSyncRun.entitiesDeleted ? `, ${o.lastSyncRun.entitiesDeleted} removed` : ''}</> : 'Never'}
            {running && <div className="vc-muted">A full refresh from Treasury is running now (about 30 minutes).</div>}
          </dd>
          <dt>Refresh schedule</dt><dd>Daily from Treasury&rsquo;s Sanctions List Service: the day&rsquo;s changes, plus a full reload weekly (or whenever the copy is empty) that also removes delisted names.</dd>
        </dl>
        <Disclosure summary={`All ${cov.programs.length} sanctions program codes in the copy`}>
          <span className="vc-chips">{cov.programs.map((p) => <span key={p.name} className="vc-chip">{p.name} · {p.count.toLocaleString()}</span>)}</span>
        </Disclosure>
        {o.recentRuns.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Disclosure summary="Recent refreshes">
              <table className="vc-table">
                <thead><tr><th>Started</th><th>Kind</th><th>Status</th><th>Updated</th><th>Removed</th><th>Started by</th></tr></thead>
                <tbody>
                  {o.recentRuns.map((r) => (
                    <tr key={r.startedAt}>
                      <td>{fmtDate(r.startedAt)}</td><td>{r.syncMode}</td><td>{r.status}</td>
                      <td>{r.entitiesUpserted ?? '—'}</td><td>{r.entitiesDeleted ?? '—'}</td><td>{r.triggerSource ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Disclosure>
          </div>
        )}
      </div>

      <div>
        <SectionTitle>Settings that change these results</SectionTitle>
        <p className="vc-hint" style={{ marginBottom: 6 }}>
          Each run reads these fresh, so a change shows on the next run. The FraudPoint cut-offs are edited in &ldquo;Risk-band cut-offs&rdquo; above;
          the officer override is below; the rest belong to IT and are changed in the Admin Panel.
        </p>
        <SettingsTable rows={[
          ...e.settings,
          {
            key: 'risk.ofac.fsrOverride.enabled', value: o.fsrOverrideEnabled ? 'true' : 'false', source: 'saved', owner: 'Risk Management',
            effect: 'On a current OFAC match: whether the officer gets the "Override with compensating doc" button. Changes the officer banner, not the match result.',
          },
        ]} />
        <FsrOverrideEditor current={o.fsrOverrideEnabled} onSaved={onSaved} />
      </div>
    </>
  );
}

function FsrOverrideEditor({ current, onSaved }: { current: boolean; onSaved: () => void }): JSX.Element {
  const [value, setValue] = useState(current);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { setValue(current); }, [current]);
  const dirty = value !== current;
  const reasonOk = reason.trim().length >= 5 && reason.trim().length <= 500;

  const save = async (): Promise<void> => {
    setBusy(true); setErr(null); setMsg(null);
    try {
      await saveRiskSettingWithReason('risk.ofac.fsrOverride.enabled', value ? 'true' : 'false', reason.trim());
      setReason('');
      setMsg('Saved. Officers see the change the next time they open a person; the next OFAC run here shows the new banner.');
      onSaved();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vc-panel vc-panel--muted" style={{ marginTop: 'var(--sp-7)' }}>
      <div className="vc-panel__body">
        <label className="vc-toggle">
          <input type="checkbox" checked={value} disabled={busy} onChange={(e) => { setValue(e.target.checked); setMsg(null); }} />
          <span><strong>Let officers override a current OFAC match</strong> with a compensating document and reason</span>
        </label>
        <p className="vc-hint">
          Off (the pilot setting): a current match goes to Risk Management and the officer stops. On: the officer may record a false-positive
          override; Risk Management is notified.
        </p>
        {dirty && (
          <div className="vc-row">
            <div style={{ flex: '1 1 320px' }}>
              <Field label="Reason for change" htmlFor="vc-fsr-reason" required hint="Recorded with the change (5–500 characters)."
                error={reason.length > 0 && !reasonOk ? '5 to 500 characters.' : null}>
                <input id="vc-fsr-reason" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
              </Field>
            </div>
            <button type="button" className="vc-btn vc-btn--ghost" disabled={busy} onClick={() => { setValue(current); setReason(''); }}>Discard</button>
            <button type="button" className="vc-btn vc-btn--primary" disabled={busy || !reasonOk} onClick={() => void save()}>{busy ? 'Saving…' : 'Save'}</button>
          </div>
        )}
        {msg && <Banner tone="ok">{msg}</Banner>}
        {err && <Banner tone="err">{err}</Banner>}
      </div>
    </div>
  );
}
